import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  LoadingButton,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import {
  useCreatePolicy,
  useDeletePolicy,
  usePolicy,
  useUpdatePolicy,
  useValidateRego,
} from './Policies.api';
import {
  stateLabel,
  saveImpedimento,
  modeLabel,
  forInput,
  policyDefaults,
  type Policy,
  type PolicyFormValues,
} from './PolicyModel';
import { PolicyGeneralTab } from './PolicyGeneralTab';
import { PolicyIdentitiesTab } from './PolicyIdentitiesTab';
import { CapabilitiesInModeRego, PolicyCapabilitiesTab } from './PolicyCapabilitiesTab';
import { PolicyConditionsTab } from './PolicyConditionsTab';
import { PolicyPublishedTab, PolicyRegoTab } from './PolicyRegoTab';
import { t, tn } from '@app/i18n';
import s from './Policies.module.css';

export interface PolicyDrawerProps {
  /** `null` = **Nova política**. */
  politicaId: string | null;
  /** `rego_validacao_disponivel` do resumo (FR-082). */
  validacaoRegoDisponivel: boolean | null;
  /**
   * Aba em que o drawer abre. Padrão `geral`.
   *
   * Existe por causa do chip «definida em Rego» da lista (spec «Acesso: pessoa ×
   * agente» §4): o clique ali é um pedido de ler a expressão, e abrir em Geral
   * cobraria um segundo clique para chegar onde a informação está.
   */
  initialTab?: PolicyDrawerTab;
  onClose: () => void;
  onSalvo: (mensagem: string) => void;
}

export type PolicyDrawerTab = 'geral' | 'identities' | 'capabilities' | 'conditions' | 'rego';
type Tab = PolicyDrawerTab;

/**
 * Rótulo do impacto, concordando com a contagem que o `ConfirmDialog` desenha ao
 * lado. Com o texto fixo no plural, política de uma identidade só dizia «**1
 * identidades** deixam de receber estas concessões». Apareceu ao dirigir a tela,
 * não nos testes — a política da varredura tinha duas, e com duas está certo.
 */
function impactLabel(howMany: number): string {
  return tn('humanAccess.confirm.impact', howMany);
}

/**
 * T12 — drawer de política (Story 9).
 *
 * O drawer é a tela inteira da política: o que ela concede, a quem, sob quais
 * condições e como vai ao PDP. Salvar **ativa** publica na mesma operação — se o
 * PDP recusar, nada é gravado e o formulário fica como está (FR-083, FR-123).
 */
export function PolicyDrawer({
  politicaId,
  validacaoRegoDisponivel,
  initialTab = 'geral',
  onClose,
  onSalvo,
}: PolicyDrawerProps) {
  const [tab, setAba] = useState<Tab>(initialTab);
  /**
   * Ids das abas por instância. Eram constantes globais (`aba-geral`,
   * `painel-geral`): funcionam com um drawer na tela, mas são ids duplicados no
   * documento assim que dois coexistem, e aí o `aria-controls` de um aponta para
   * o painel do outro. Mesmo prefixo que o drawer de Identidades usa.
   */
  const idBase = useId();
  const tabID = (id: Tab) => `${idBase}-aba-${id}`;
  const idDoPainel = (id: Tab) => `${idBase}-painel-${id}`;
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  /**
   * Valores esperando a confirmação de **desativar** (ativa → rascunho).
   * `null` = nada pendente. Guardamos o formulário inteiro porque a gravação só
   * acontece depois do "sim", e é o mesmo conteúdo que o usuário viu.
   */
  const [desativacaoPendente, setDesativacaoPendente] = useState<PolicyFormValues | null>(null);
  /** Texto que passou pela validação nesta sessão do drawer. */
  const [textoValidado, setTextoValidado] = useState<string | null>(null);
  const [avisoLocal, setAvisoLocal] = useState<string | null>(null);

  const query = usePolicy(politicaId);
  const politica: Policy | undefined = query.data;
  const editando = Boolean(politicaId);

  const create = useCreatePolicy();
  const save = useUpdatePolicy();
  const remove = useDeletePolicy();
  const validate = useValidateRego();

  const canWrite = useCanOperar(editando ? 'updatePolicy' : 'createPolicy');
  const canDelete = useCanOperar('deletePolicy');

  const { control, handleSubmit, watch, setValue, formState, reset } = useForm<PolicyFormValues>({
    defaultValues: policyDefaults(politica),
  });

  /*
   * O registro chega depois do primeiro render: repovoa o formulário quando ele
   * chega.
   *
   * Isto era um `if (politica && !semeada) { reset(...) }` no corpo do
   * componente, e o `reset` atualizava os `Controller` **durante o render** do
   * drawer — o `react-dom` avisava "Cannot update a component while rendering a
   * different component". O aviso só existe em dev, então nenhum teste o pegava;
   * apareceu ao dirigir a tela no navegador. É o mesmo `useEffect` que os
   * drawers de identidade e de resource já usam.
   */
  useEffect(() => {
    if (politica) reset(policyDefaults(politica));
  }, [politica, reset]);

  const valores = watch();
  const editavel = canWrite;

  const regoValidated =
    textoValidado === valores.rego_text ||
    Boolean(politica?.rego_validated && politica.rego_text === valores.rego_text);

  const pendente = create.isPending || save.isPending;
  const writeError = create.error ?? save.error;

  /** Grava a alteração de uma política existente. Sai do `aoSubmeter` porque a
   *  desativação passa pela confirmação antes de chegar aqui. */
  function storeChange(payload: PolicyFormValues) {
    if (!politica) return;
    save.mutate(
      { id: politica.id, body: forInput(payload, politica) },
      {
        onSuccess: (updated) => {
          setDesativacaoPendente(null);
          onSalvo(
            updated.state === 'ativa'
              ? t('humanAccess.toast.published', {
                  name: updated.name,
                  version: updated.version,
                })
              : t('humanAccess.toast.savedDraft', { name: updated.name }),
          );
        },
      },
    );
  }

  function aoSubmeter(payload: PolicyFormValues) {
    const lockout = saveImpedimento(payload, {
      validacaoDisponivel: validacaoRegoDisponivel !== false,
      regoValidado: regoValidated,
    });
    if (lockout) {
      // Salta para a aba que precisa de atenção, como o protótipo faz.
      setAba(lockout.aba === 'geral' ? 'geral' : lockout.aba);
      setAvisoLocal(lockout.message);
      return;
    }
    setAvisoLocal(null);

    if (politica) {
      // Desativar tira as concessões do PDP exatamente como excluir tira: as
      // identidades param de receber o que a política concedia. Se excluir pede
      // confirmação, desativar também pede — ninguém perde acesso em silêncio
      // (cenário 9.3). O `mutate` só acontece depois do "sim".
      if (politica.state === 'ativa' && !payload.ativa) {
        setDesativacaoPendente(payload);
        return;
      }
      storeChange(payload);
      return;
    }
    create.mutate(forInput(payload, politica), {
      onSuccess: (isNew) =>
        onSalvo(
          isNew.state === 'ativa'
            ? t('humanAccess.toast.published', { name: isNew.name, version: isNew.version })
            : t('humanAccess.toast.savedDraft', { name: isNew.name }),
        ),
    });
  }

  // AS TRÊS PERGUNTAS DA REGRA (P6.2 item 4; renomeadas em 03/09)
  //
  //   A regra responde três perguntas, uma aba cada: «Quem pede» (as claims do
  //   JWT) → «Identities» (quais identidades a pessoa alcança) → «Capabilities»
  //   (quais tools ela usa). Antes eram «Condições» e «Identidades» — dois
  //   substantivos que não diziam de qual EIXO cada aba falava, e a confusão
  //   entre os eixos é o que a P6.1 diagnosticou e a P6.2 mandou cercar.
  //
  //   «Por qual agente» virou «Identities» com a reorganização Human × Identity
  //   Access: o dado é o mesmo (`all_identities`/lista), mudou a moldura — de
  //   escopo («por qual agente a regra vale») para concessão («quais identidades
  //   a pessoa pode acessar»). O nome bate com o das entidades da T4, que é o
  //   que a aba lista.
  //
  //   A contagem mostra «todas» em vez de um número quando o atalho está ligado:
  //   `0` ali diria «nenhuma identidade» sobre a regra que cobre todas.
  const abas: {
    id: Tab;
    rotulo: string;
    contagem?: number;
    marca?: string;
    desabilitada?: boolean;
  }[] = [
    { id: 'geral', rotulo: t('humanAccess.drawer.tab.general') },
    {
      id: 'conditions',
      rotulo: t('humanAccess.drawer.tab.who'),
      contagem: valores.mode === 'rego' ? undefined : valores.claims.length,
      desabilitada: valores.mode === 'rego',
    },
    {
      id: 'identities',
      rotulo: t('humanAccess.drawer.tab.identities'),
      contagem: valores.allIdentities ? undefined : valores.identities.length,
      marca: valores.allIdentities ? t('humanAccess.drawer.tab.identities.all') : undefined,
      desabilitada: valores.mode === 'rego',
    },
    {
      id: 'capabilities',
      rotulo: t('humanAccess.drawer.tab.capabilities'),
      contagem: valores.mode === 'rego' ? undefined : valores.grants.length,
    },
    {
      id: 'rego',
      rotulo: t(
        valores.mode === 'rego'
          ? 'humanAccess.drawer.tab.rego'
          : 'humanAccess.drawer.tab.published',
      ),
    },
  ];

  /**
   * Trocar para Rego manual estando em Identidades ou Condições deixaria a
   * seleção numa aba que acabou de ficar desabilitada — painel aberto, aba
   * apagada e sem como voltar pelo teclado. Volta para Geral, que existe nos
   * dois modos.
   */
  const tabCurrentDesabilitada = abas.find((item) => item.id === tab)?.desabilitada ?? false;
  useEffect(() => {
    if (tabCurrentDesabilitada) setAba('geral');
  }, [tabCurrentDesabilitada]);

  /**
   * «Anterior» / «Próximo» no rodapé (ajustes-3, 28/08, item 11.1): o PO
   * preenche a política como um passo a passo, e a única saída de cada aba era
   * a barra de abas lá em cima. Só navegação — as abas continuam clicáveis e
   * salvar continua sendo o botão de sempre. Abas desabilitadas (Rego manual)
   * são puladas, como a barra já faz.
   */
  const abasNavegaveis = abas.filter((item) => !item.desabilitada).map((item) => item.id);
  const posicao = abasNavegaveis.indexOf(tab);
  const previousTab = posicao > 0 ? abasNavegaveis[posicao - 1] : null;
  const tabFollowing =
    posicao >= 0 && posicao < abasNavegaveis.length - 1 ? abasNavegaveis[posicao + 1] : null;

  return (
    <>
      <Drawer
        open
        title={
          editando
            ? (politica?.name ?? t('humanAccess.drawer.title.fallback'))
            : t('humanAccess.drawer.title.new')
        }
        subtitle={t('humanAccess.drawer.subtitle')}
        dirty={formState.isDirty && !pendente}
        onClose={onClose}
        discardMessage={t('humanAccess.drawer.discard')}
        footer={
          <DrawerFooter
            start={
              <>
                {editando && canDelete ? (
                  <Button
                    variant="danger"
                    icon="trash"
                    onClick={() => setConfirmandoExclusao(true)}
                  >
                    {t('humanAccess.drawer.delete')}
                  </Button>
                ) : null}
                <Button
                  variant="out"
                  icon="chevron-left"
                  disabled={!previousTab}
                  onClick={() => previousTab && setAba(previousTab)}
                >
                  {t('humanAccess.drawer.previous')}
                </Button>
                <Button
                  variant="out"
                  disabled={!tabFollowing}
                  onClick={() => tabFollowing && setAba(tabFollowing)}
                >
                  {t('humanAccess.drawer.next')}
                </Button>
              </>
            }
          >
            <Button variant="out" onClick={onClose}>
              {t('humanAccess.drawer.cancel')}
            </Button>
            {editavel ? (
              <LoadingButton
                loading={pendente}
                loadingLabel={t('humanAccess.drawer.saving')}
                onClick={handleSubmit(aoSubmeter)}
              >
                {editando
                  ? t('humanAccess.drawer.save', { version: (politica?.version ?? 0) + 1 })
                  : t('humanAccess.drawer.create')}
              </LoadingButton>
            ) : null}
          </DrawerFooter>
        }
      >
        {query.isLoading ? <p className={s.vazio}>{t('humanAccess.drawer.loading')}</p> : null}

        {query.isError ? (
          <ErrorBanner
            error={query.error}
            title={t('humanAccess.drawer.openError')}
            onRetry={() => void query.refetch()}
            hideLink
          />
        ) : null}

        {politica ? (
          <div className={s.badgesTopo}>
            <Badge tone={politica.mode === 'rego' ? 'warning' : 'primary'}>
              {modeLabel(politica.mode)}
            </Badge>
            <Badge tone={politica.state === 'ativa' ? 'success' : 'neutral'}>
              {stateLabel(politica)}
            </Badge>
            {politica.updated_by ? (
              <span className={s.nota}>
                {t('humanAccess.drawer.updatedBy', { user: politica.updated_by })}
              </span>
            ) : null}
          </div>
        ) : null}

        {writeError ? (
          // `pdp_recusou`/`pdp_inalcancavel` mantêm o formulário; `app_desconhecida_na_politica`
          // traz a lista de identidades que o middleware não conhece (FR-089).
          <ErrorBanner
            error={writeError}
            escrita
            title={t('humanAccess.drawer.saveError')}
            hideLink
          />
        ) : null}

        {avisoLocal ? (
          <Alert tone="warning" title={t('humanAccess.drawer.missingData')}>
            {avisoLocal}
          </Alert>
        ) : null}

        <div className={s.tablist} role="tablist" aria-label={t('humanAccess.drawer.tablist.aria')}>
          {abas.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={tabID(item.id)}
              aria-selected={tab === item.id}
              aria-controls={idDoPainel(item.id)}
              // `desabilitada` era calculado e nunca aplicado: em Rego manual as
              // abas Identidades e Condições ficavam clicáveis contra a intenção
              // escrita logo acima. `disabled` bloqueia teclado e mouse;
              // `aria-disabled` diz o porquê a quem ouve a aba.
              disabled={item.desabilitada}
              aria-disabled={item.desabilitada || undefined}
              className={[s.tab, tab === item.id ? s.tabOn : '', item.desabilitada ? s.tabOff : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setAba(item.id)}
            >
              {item.rotulo}
              {item.marca !== undefined ? (
                <span className={s.tabContagem}>{item.marca}</span>
              ) : item.contagem !== undefined ? (
                <span className={s.tabContagem}>{item.contagem}</span>
              ) : null}
            </button>
          ))}
        </div>

        <div
          id={idDoPainel(tab)}
          role="tabpanel"
          aria-labelledby={tabID(tab)}
          tabIndex={0}
          className={s.painel}
        >
          {tab === 'geral' ? (
            <PolicyGeneralTab
              control={control}
              valores={valores}
              editable={editavel}
              selecionadas={valores.identities.map((id) => {
                const conhecida = politica?.identities.find(
                  (identidade) => identidade.identity_id === id,
                );
                return conhecida ?? { identity_id: id, slug: id.slice(0, 8) };
              })}
            />
          ) : null}

          {tab === 'identities' ? (
            valores.mode === 'rego' ? (
              <CapabilitiesInModeRego />
            ) : (
              <PolicyIdentitiesTab
                valor={valores.identities}
                editable={editavel}
                onChange={(ids) => setValue('identities', ids, { shouldDirty: true })}
                allIdentities={valores.allIdentities}
                onAllIdentitiesChange={(todas) =>
                  setValue('allIdentities', todas, { shouldDirty: true })
                }
              />
            )
          ) : null}

          {tab === 'capabilities' ? (
            valores.mode === 'rego' ? (
              <CapabilitiesInModeRego />
            ) : (
              <PolicyCapabilitiesTab
                concessoes={valores.grants}
                onConcessoesChange={(grants) => setValue('grants', grants, { shouldDirty: true })}
                editable={editavel}
              />
            )
          ) : null}

          {tab === 'conditions' ? (
            valores.mode === 'rego' ? (
              <CapabilitiesInModeRego />
            ) : (
              <PolicyConditionsTab
                claims={valores.claims}
                editable={editavel}
                onClaimsChange={(claims) => setValue('claims', claims, { shouldDirty: true })}
              />
            )
          ) : null}

          {tab === 'rego' ? (
            valores.mode === 'rego' ? (
              <PolicyRegoTab
                texto={valores.rego_text}
                onTextoChange={(texto) => setValue('rego_text', texto, { shouldDirty: true })}
                slug={politica?.slug ?? null}
                editable={editavel}
                validacaoDisponivel={validacaoRegoDisponivel}
                validado={regoValidated}
                validando={validate.isPending}
                resultado={validate.data}
                erroValidacao={validate.error}
                onValidar={() =>
                  validate.mutate(
                    { rego: valores.rego_text, policy_id: politica?.id ?? null },
                    {
                      onSuccess: (resultado) =>
                        setTextoValidado(resultado.ok ? valores.rego_text : null),
                    },
                  )
                }
              />
            ) : (
              <PolicyPublishedTab publicado={politica?.published ?? null} />
            )
          ) : null}
        </div>
      </Drawer>

      {/* Desativar (ativa → rascunho): mesma porta que excluir, porque o efeito no
          PDP é o mesmo. O impacto vem com contagem, como o FR-130 pede. */}
      <ConfirmDialog
        open={desativacaoPendente !== null}
        title={t('humanAccess.confirm.draft.title', {
          name: politica?.name ?? t('humanAccess.confirm.thisRule'),
        })}
        message={t('humanAccess.confirm.stopsApplying')}
        impact={
          politica
            ? [
                {
                  label: impactLabel(politica.identities.length),
                  count: politica.identities.length,
                },
              ]
            : undefined
        }
        destructive
        confirmLabel={t('humanAccess.confirm.draft.label')}
        loading={save.isPending}
        error={
          save.error ? (
            <ErrorBanner error={save.error} escrita title={t('humanAccess.draftError')} hideLink />
          ) : null
        }
        onCancel={() => {
          // Desistir tem de desfazer o que a desistência nega: com o toggle
          // desmarcado, a tela diria "rascunho" sobre uma política que continua
          // ativa no servidor.
          setDesativacaoPendente(null);
          setValue('ativa', true, { shouldDirty: true });
        }}
        onConfirm={() => {
          if (desativacaoPendente) storeChange(desativacaoPendente);
        }}
      />

      <ConfirmDialog
        open={confirmandoExclusao}
        title={t('humanAccess.confirm.delete.title', {
          name: politica?.name ?? t('humanAccess.confirm.thisRule'),
        })}
        message={t(
          politica?.state === 'ativa'
            ? 'humanAccess.confirm.stopsApplying'
            : 'humanAccess.confirm.draftNotPublished',
        )}
        impact={
          politica
            ? [
                {
                  label: impactLabel(politica.identities.length),
                  count: politica.identities.length,
                },
              ]
            : undefined
        }
        destructive
        confirmLabel={t('humanAccess.confirm.delete.label')}
        loading={remove.isPending}
        error={
          remove.error ? (
            <ErrorBanner
              error={remove.error}
              escrita
              title={t('humanAccess.deleteError')}
              hideLink
            />
          ) : null
        }
        onCancel={() => setConfirmandoExclusao(false)}
        onConfirm={() => {
          if (!politica) return;
          remove.mutate(politica.id, {
            onSuccess: () => {
              setConfirmandoExclusao(false);
              onSalvo(t('humanAccess.drawer.removed', { name: politica.name }));
            },
          });
        }}
      />
    </>
  );
}
